import { RuntimeNodeBase } from './RuntimeNodeBase';
import { PropMechanicsDispatchResult, PropRuntimeEvent, PropRuntimeEventReason, RuntimePropInstance } from '../PropMechanicsTypes';
import { isEmptyPropInstance } from './RuntimeNodeUtils';

/**
 * 事件节点基类。
 *
 * 事件节点不提供数据输出端口；不同事件节点可以选择监听事件总线，或由 BattleManager 显式调用。
 * 事件节点自身负责完成命中判断和 flow 推进。
 */
export abstract class EventRuntimeNodeBase extends RuntimeNodeBase {
    /**
     * 执行当前事件节点的后续 flow。
     *
     * 事件节点已经在自己的监听回调中完成命中判断，因此这里仅负责把事件上下文交给节点运行时继续执行。
     *
     * @param event 触发当前事件节点的运行时事件。
     */
    protected executeFlow(event: PropRuntimeEvent): PropMechanicsDispatchResult {
        return this.context.executeEntry(this, event);
    }
}

/**
 * 回合开始事件节点。
 *
 * `interval` 表示间隔轮数，实际周期为 `interval + 1`；`loop = 0` 或空值表示不限次数。
 */
export class EventTurnStartNode extends EventRuntimeNodeBase {
    /**
     * 当前倒计时。
     *
     * 该字段属于节点实例，因此同一个道具实例会保留自己的倒计时状态。
     * `interval = 0` 时周期为 1，实例化后的第一个回合开始事件即可触发。
     */
    private _timer = this.getPeriod();
    /** 已成功触发次数；用于执行 loop 限制，0 表示尚未触发。 */
    private triggeredCount = 0;

    /**
     * 注册入手时的计时器展示刷新。
     *
     * 回合开始节点不监听 `roundStarted`。回合推进时由 BattleManager 按道具顺序显式调用 `trigger()`，
     * 避免每个道具都向事件总线广播一次“回合开始”。
     */
    public initialize(): void {
        this.context.listen('propAcquired', (event) => {
            if (event.item?.id !== this.context.owner.id) return;
            this.emitTimerChanged('acquired');
        });
        this.context.listen('propEnteredBoard', (event) => {
            if (event.item?.id !== this.context.owner.id) return;
            if (event.reason === 'propAcquired' || event.reason === 'actionSpawn' || event.reason === 'snapshotRestore') return;
            this.emitTimerChanged(event.reason);
        });
    }

    /**
     * 主动发布当前倒计时值。
     *
     * 该方法用于转化等不会重新触发 `propAcquired` 的流程。它只通知显示层刷新当前计时器，
     * 不改变计时器数值，也不会触发后续 flow。
     *
     * @param reason 计时器刷新原因，便于日志和调试区分来源。
     * @param source 造成刷新动作的来源道具。
     */
    public emitCurrentTimer(reason: PropRuntimeEventReason, source?: RuntimePropInstance): void {
        this.emitTimerChanged(reason, source);
    }

    /**
     * 由 BattleManager 按道具顺序显式触发回合开始入口。
     *
     * 该方法不监听事件总线；它只在当前实例被 BattleManager 选中执行时推进本节点计时器。
     *
     * @param event 本次道具回合开始执行上下文。
     * @returns 倒计时归零并执行 flow 时返回执行结果，否则返回 undefined。
     */
    public trigger(event: PropRuntimeEvent): PropMechanicsDispatchResult | undefined {
        const owner = this.context.owner;
        if (!canOwnerReceiveRoundStart(owner)) return undefined;
        if (this.isLoopCompleted()) return undefined;
        return this.setTimer(this.timer - 1, 'turnStart', undefined, event);
    }

    /**
     * 调整当前节点倒计时。
     *
     * @param amount 倒计时偏移量；负数表示提前触发，正数表示延后触发。
     */
    public adjustTimer(amount: number, source?: RuntimePropInstance): void {
        if (this.getPeriod() <= 1) return;
        this.setTimer(this.timer + Math.floor(amount), 'accelerate', source, this.context.event);
    }

    /** 保存回合开始节点的倒计时和循环次数。 */
    public serializeState(): { timer: number; triggeredCount: number } {
        return {
            timer: this.timer,
            triggeredCount: this.triggeredCount,
        };
    }

    /** 恢复回合开始节点的倒计时和循环次数；恢复过程不触发 flow。 */
    public restoreState(state: unknown): void {
        if (!state || typeof state !== 'object') return;
        const raw = state as Partial<{ timer: unknown; triggeredCount: unknown }>;
        const timer = Number(raw.timer);
        const triggeredCount = Number(raw.triggeredCount);
        if (Number.isFinite(timer)) this._timer = Math.max(0, Math.floor(timer));
        if (Number.isFinite(triggeredCount)) this.triggeredCount = Math.max(0, Math.floor(triggeredCount));
    }

    private get timer(): number {
        return this._timer;
    }

    private setTimer(
        value: number,
        reason: PropRuntimeEventReason,
        source?: RuntimePropInstance,
        event?: PropRuntimeEvent,
        triggerOnZero = true,
    ): PropMechanicsDispatchResult | undefined {
        const nextValue = Math.max(0, Math.floor(value));
        if (nextValue === this._timer) return undefined;
        this._timer = nextValue;
        this.emitTimerChanged(reason, source);
        if (!triggerOnZero || nextValue > 0) return undefined;
        return this.triggerFlowWhenTimerReachedZero(event ?? this.context.event);
    }

    private triggerFlowWhenTimerReachedZero(event: PropRuntimeEvent): PropMechanicsDispatchResult | undefined {
        if (this.isLoopCompleted()) return undefined;
        this.triggeredCount += 1;
        this.context.owner.triggerCount += 1;
        const result = this.executeFlow(event);
        if (!this.isLoopCompleted()) this.setTimer(this.getPeriod(), 'reset', undefined, event, false);
        return result;
    }

    private isLoopCompleted(): boolean {
        const loop = this.getLoop();
        return loop > 0 && this.triggeredCount >= loop;
    }

    private emitTimerChanged(reason: PropRuntimeEventReason, source?: RuntimePropInstance): void {
        if (!this.shouldEmitTimer()) return;
        const owner = this.context.owner;
        if (!canOwnerReceiveEvent(owner)) return;
        this.context.emit({ type: 'propTimerChanged', item: owner, source, timerValue: this.timer, reason });
    }

    private shouldEmitTimer(): boolean {
        return this.getPeriod() > 1;
    }

    private getPeriod(): number {
        return this.getInterval() + 1;
    }

    private getInterval(): number {
        return Math.max(0, Math.floor(Number(this.node.data?.interval ?? 0)));
    }

    private getLoop(): number {
        return Math.max(0, Math.floor(Number(this.node.data?.loop ?? 0)));
    }
}

/**
 * 持有触发事件节点。
 *
 * 当前实现将其绑定到 `propAcquired`，表示实例入手后生效；持续效果由动作节点写入状态。
 */
export class EventAcquireNode extends EventRuntimeNodeBase {
    private released = false;
    private releaseCallbacks: Array<() => void> = [];

    /**
     * 初始化持有触发。
     *
     * 持有触发只在实例正式入手时生效；初始化阶段只注册入手和移除监听，不会立即执行 flow。
     * 后续动作登记的释放回调会由该节点保存，并在道具移除、转化重建或节点运行时销毁时统一执行。
     */
    public initialize(): void {
        if (!canOwnerReceiveEvent(this.context.owner)) return;
        this.context.listen('propAcquired', (event) => this.executeIfOwnerAcquired(event));
        this.context.listen('propRemoved', (event) => this.releaseIfOwnerLost(event));
    }

    /**
     * 销毁持有触发节点，释放已经写入全局状态的持有效果。
     */
    public dispose(): void {
        this.releaseHeldEffect('disposeAcquireEffect');
    }

    private executeIfOwnerAcquired(event: PropRuntimeEvent): void {
        if (this.released) return;
        if (event.item?.id !== this.context.owner.id) return;
        const result = this.executeFlow(event);
        if (!result.releaseCallbacks) result.releaseCallbacks = [];
        this.releaseCallbacks = result.releaseCallbacks;
    }

    private releaseIfOwnerLost(event: PropRuntimeEvent): void {
        const ownerId = this.context.owner.id;
        if (event.item?.id !== ownerId) return;
        this.releaseHeldEffect('propRemoved');
    }

    private releaseHeldEffect(reason: string): void {
        if (this.released) return;
        this.released = true;
        for (const callback of [...this.releaseCallbacks].reverse()) {
            try {
                callback();
            } catch (error) {
                console.warn(`[${this.context.owner.graph.name}] 持有触发释放失败(${reason})`, error);
            }
        }
        this.releaseCallbacks.length = 0;
        this.context.owner.meta.acquireEffectReleased = true;
    }
}

/**
 * 主动使用事件节点。
 *
 * 用于辅料或可主动使用道具，只有该实例被主动使用时才触发。
 */
export class EventUseNode extends EventRuntimeNodeBase {
    /**
     * 注册主动使用监听。
     *
     * 只有当前实例收到 `propUsed` 事件时才执行 flow，通常用于辅料或主动技能类道具。
     */
    public initialize(): void {
        const owner = this.context.owner;
        if (!canOwnerReceiveEvent(owner)) return;
        this.context.listen('propUsed', (event) => {
            if (event.type !== 'propUsed' || event.item?.id !== owner.id) return;
            this.executeFlow(event);
        });
    }
}

/**
 * 被消除事件节点。
 *
 * 默认在自身被消除时触发；如果连接了 target 输入，则由引擎额外判断目标集合中是否有实例被消除。
 */
export class EventEliminatedNode extends EventRuntimeNodeBase {
    /**
     * 注册被消除监听。
     *
     * 默认监听自身被消除；如果连接了 target 输入，则监听目标集合中任意实例被消除。
     */
    public initialize(): void {
        const owner = this.context.owner;
        if (isEmptyPropInstance(owner)) return;
        this.context.listen('propEliminated', (event) => {
            if (event.type !== 'propEliminated') return;
            const removedTarget = event.target ?? event.item;
            if (!removedTarget || !isEventTargetOnBoard(event, removedTarget)) return;

            const targetPortIds = this.context.readInputNodeIds(this.node, 'target');
            if (targetPortIds.length) {
                const targets = this.context.readInputTargets(this.node, 'target', [owner]);
                const isTargetSelfMatched = targetPortIds.some((nodeId) => {
                    const inputNode = this.context.getNode(nodeId);
                    return inputNode?.type === 'targetSelf' && removedTarget.id === owner.id;
                });
                if (!isTargetSelfMatched && !targets.some((target) => target.id === removedTarget.id)) return;
                this.executeFlow(event);
                return;
            }

            if (removedTarget.id !== owner.id) return;
            this.executeFlow(event);
        });
    }
}

function isEventTargetOnBoard(event: PropRuntimeEvent, target: RuntimePropInstance): boolean {
    const row = event.row ?? target.row;
    const col = event.col ?? target.col;
    return row >= 0 && col >= 0;
}

function canOwnerReceiveEvent(owner: RuntimePropInstance): boolean {
    return owner.alive && !isEmptyPropInstance(owner);
}

function canOwnerReceiveRoundStart(owner: RuntimePropInstance): boolean {
    return canOwnerReceiveEvent(owner);
}
